import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Box } from '@material-ui/core';
import { rgba } from 'polished';
import type Arranger from '@this/src/domain/arranger/arranger';
import type OrderItemStepTodo from '@this/src/domain/arrangement/order_item_step_todo';
import type { OrderItemStepAction } from '@this/src/domain/arrangement/order_item_step_todo';
import { ORDER_ITEM_STEP_ACTIONS } from '@this/src/domain/arrangement/order_item_step_todo';
import { OrderItemCategory } from '@this/src/domain/order_item/order_item_category';
import { styled } from '@this/src/components/constants/themes';
import { Link } from '@this/src/components/shared/ui/navigations/link';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import { TableCell, TableRow } from '@this/src/components/shared/ui/data_displays/table';
import { OrderItemJobType } from '@this/src/domain/order_item/order_item_job_type';
import { OrderItemStatus } from '@this/src/domain/order_item/order_item_status';
import { FindTicketingTypeByValue } from '@this/domain/arrangement/ticketing_type_query';
import TripStatusIndicator from '../../shared/trip_status_indicator';
import LogModal from '../log_modal';

interface Props {
  arranger: Arranger;
  todo: OrderItemStepTodo;
  loading: boolean;
  onTodoAction: (todo: OrderItemStepTodo, action: OrderItemStepAction) => void;
  onMessageFinish: (messageId: number) => void;
  onWorkDialogOpen: (todo: OrderItemStepTodo) => (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onSequenceAbortOpen: (
    todo: OrderItemStepTodo
  ) => (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onArrangerOpen: (todo: OrderItemStepTodo) => (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onAssignToMe: (todo: OrderItemStepTodo) => void;
  assignType: string;
  serviceId: number;
}

interface State {
  showLogModal: boolean;
}

const OrderItemStepTodoTableRow = observer(
  ({
    arranger,
    todo,
    loading,
    onTodoAction,
    onMessageFinish,
    onWorkDialogOpen,
    onSequenceAbortOpen,
    onArrangerOpen,
    onAssignToMe,
    assignType,
    serviceId
  }: Props) => {
    const [state, setState] = useState<State>({ showLogModal: false });
    const { showLogModal } = state;
    const allowChangeArranger = useMemo(
      () => ['queue', 'todo', 'started', 'suspended'].includes(todo.stepStatus),
      [todo.stepStatus]
    );

    return (
      <TableRow style={{ background: todo.isUrgent ? rgba('red', 0.3) : 'auto' }}>
        <TableCell nowrap>
          <TripStatusIndicator status={todo.tripStatus} />
        </TableCell>
        <TableCell>
          {todo.jobType !== undefined && (
            <span>
              {OrderItemJobType[todo.jobType]}-{OrderItemStatus[todo.status]}
            </span>
          )}
        </TableCell>
        <TableCell>{todo.arrangerName}</TableCell>
        <TableCell nowrap>{todo.category !== undefined && OrderItemCategory[todo.category]}</TableCell>
        <TableCell>
          {todo.todoType === 'order_item_step' && (
            <TextWithChanged>
              {todo.sequenceName}
              <Button size="small" color="danger" loading={loading} onClick={onSequenceAbortOpen(todo)}>
                破棄
              </Button>
            </TextWithChanged>
          )}
        </TableCell>
        <TableCell>{todo.changedStatus ? '有り' : todo.changedStatus === false ? '無し' : null}</TableCell>
        <TableCell>{todo.organizationName}</TableCell>
        <TableCell>{todo.sp ? '◯' : ''}</TableCell>
        <TableCell>{todo.useQr ? '◯' : ''}</TableCell>
        <TableCell>{todo.premiumSupport ? '◯' : ''}</TableCell>
        <TableCell>{todo.waiting ? '◯' : ''}</TableCell>
        <TableCell>
          {todo.ticketingType && todo.ticketingType.length > 0
            ? todo.ticketingType.map((ticketing: number, i: number) => (
                <div key={i}>{FindTicketingTypeByValue(serviceId, ticketing)}</div>
              ))
            : ''}
        </TableCell>
        <TableCell>
          {todo.airTicketType.map((type, i) => (
            <div key={i}>{type}</div>
          ))}
        </TableCell>
        <TableCell nowrap>{todo.startDate?.format('YYYY-MM-DD')}</TableCell>
        <TableCell nowrap>{todo.receivedAt?.format('YYYY-MM-DD HH:mm')}</TableCell>
        <TableCell nowrap>{todo.purchaseDeadline?.format('YYYY-MM-DD HH:mm')}</TableCell>
        <TableCell>
          {todo.lastUserMessage?.split('\n').map((line, i) => (
            <Box key={i}>{line}</Box>
          ))}
        </TableCell>
        <TableCell nowrap>
          <Link
            href={`/arrangement/virtual_counter?trip_id=${todo.tripId}`}
            target="_blank"
            rel="noopener noreffer"
          >
            {todo.tripId}
          </Link>
        </TableCell>
        <TableCell nowrap>{todo.traceId}</TableCell>
        <TableCell>
          {todo.lastLogMessage && <span>{todo.lastLogMessage}</span>}
          {todo.lastLogMessageArranger && <span>({todo.lastLogMessageArranger})</span>}
          <p>
            (<Link onClick={() => setState(state => ({ ...state, showLogModal: true }))}>ログ</Link>)
          </p>
        </TableCell>
        <TableCell>
          {todo.todoType === 'order_item_step' && todo.snoozeTo && (
            <Box style={{ marginBottom: '8px', fontSize: '0.75rem', color: '#666' }}>
              スヌーズ解除: {todo.snoozeTo.format('YYYY-MM-DD HH:mm')}
            </Box>
          )}
          <Actions>
            {todo.todoType === 'order_item_step' && (
              <Button
                size="small"
                color="sub"
                loading={loading}
                disabled={!allowChangeArranger}
                onClick={onArrangerOpen(todo)}
              >
                アサイン変更
              </Button>
            )}
            {todo.todoType === 'message' && todo.lastUserMessageId && (
              <Button
                size="small"
                loading={loading}
                onClick={() => todo.lastUserMessageId && onMessageFinish(todo.lastUserMessageId)}
              >
                対応不要
              </Button>
            )}
            {todo.actions(arranger).map(
              action =>
                action !== 'abort' && (
                  <Button
                    key={action}
                    size="small"
                    color={action === 'release' ? 'sub' : 'primary'}
                    loading={loading}
                    onClick={() => onTodoAction(todo, action)}
                  >
                    {ORDER_ITEM_STEP_ACTIONS[action]}
                  </Button>
                )
            )}
            {todo.stepStatus === 'started' && (
              <Button size="small" loading={loading} onClick={onWorkDialogOpen(todo)}>
                作業
              </Button>
            )}
          </Actions>
        </TableCell>
        {showLogModal && todo.traceId && (
          <LogModal
            traceId={todo.traceId}
            onClose={() => setState(state => ({ ...state, showLogModal: false }))}
          />
        )}
      </TableRow>
    );
  }
);

const TextWithChanged = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

export default OrderItemStepTodoTableRow;
